import React, { useState, useEffect } from 'react';
import { searchInventoryProducts, getAvailableBrands, InventoryProduct } from '../../services/inventoryApi';
// Removed unused icon imports

interface InventorySearchProps {
  selectedDate: string;
  canEdit: boolean;
}

const InventorySearch: React.FC<InventorySearchProps> = ({ selectedDate, canEdit }) => {
  const [searchMethod, setSearchMethod] = useState<'filter' | 'direct'>('direct');
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<InventoryProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBrands();
  }, []);

  useEffect(() => {
    if (searchQuery || selectedBrand) {
      performSearch();
    } else {
      setSearchResults([]);
    }
  }, [searchMethod, selectedBrand, searchQuery, selectedDate]);

  const loadBrands = async () => {
    try {
      const response = await getAvailableBrands();
      setAvailableBrands(response.brands || []);
    } catch (error) {
      console.error('Error loading brands:', error);
    }
  };

  const performSearch = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        method: searchMethod,
        date: selectedDate,
        ...(searchMethod === 'filter' && selectedBrand && { category: selectedBrand }),
        ...(searchQuery && { query: searchQuery })
      };

      const response = await searchInventoryProducts(params);
      setSearchResults(response.products || []);
    } catch (error: any) {
      console.error('Search error:', error);
      setError(error.response?.data?.message || 'Search failed');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    performSearch();
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSelectedBrand('');
    setSearchResults([]);
    setError(null);
  };

  // Group results by category
  const groupedResults = searchResults.reduce((acc, product) => {
    if (!acc[product.category]) {
      acc[product.category] = [];
    }
    acc[product.category].push(product);
    return acc;
  }, {} as Record<string, InventoryProduct[]>);

  return (
    <div className="space-y-6">
      {/* Search Controls */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <span className="mr-2">🔍</span>
          Search Inventory Products
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Search Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Method</label>
            <select
              value={searchMethod}
              onChange={(e) => setSearchMethod(e.target.value as 'filter' | 'direct')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="direct">Direct Search</option>
              <option value="filter">Filter by Brand</option>
            </select>
          </div>

          {/* Brand Selector (only for filter method) */}
          {searchMethod === 'filter' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Brand</option>
                {availableBrands.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Search Query */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {searchMethod === 'filter' ? 'Search within brand' : 'Search products'}
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={searchMethod === 'filter' ? 'Search within selected brand...' : 'Search by product name or number...'}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-end space-x-2">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <span className={`mr-2 ${loading ? 'animate-spin' : ''}`}>🔍</span>
              {loading ? 'Searching...' : 'Search'}
            </button>
            <button
              onClick={clearSearch}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Search Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <p className="text-sm text-blue-800">
            <strong>Search Tips:</strong>
            {searchMethod === 'direct' 
              ? ' Enter product name or product number to search across all brands.'
              : ' First select a brand, then search for specific products within that brand.'
            }
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <span className="mr-2">📊</span>
              Search Results ({searchResults.length} products found)
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Products with inventory data for {selectedDate}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product Details
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Opening Stock
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Purchase
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sales
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Closing Stock
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rate (Rs)
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(groupedResults).map(([category, products]) => (
                  <React.Fragment key={category}>
                    {/* Category Header */}
                    <tr className="bg-gray-100">
                      <td colSpan={7} className="px-6 py-3">
                        <h4 className="text-sm font-semibold text-gray-800">{category}</h4>
                      </td>
                    </tr>
                    
                    {/* Products in Category */}
                    {products.map((product) => (
                      <tr key={product.productId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{product.productName}</div>
                            <div className="text-sm text-gray-500">{product.productNo}</div>
                            <div className="text-xs text-gray-400">{product.unit}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          {(product.openingStock || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          {(product.purchase || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          {(product.sales || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {(product.closingStock || 0).toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          ₹{(product.rate || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            product.hasInventory 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {product.hasInventory ? 'Has Data' : 'No Data'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 px-6 py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-lg font-bold text-blue-600">{searchResults.length}</div>
                <div className="text-xs text-gray-600">Total Products</div>
              </div>
              <div>
                <div className="text-lg font-bold text-green-600">
                  {searchResults.filter(p => p.hasInventory).length}
                </div>
                <div className="text-xs text-gray-600">With Inventory</div>
              </div>
              <div>
                <div className="text-lg font-bold text-purple-600">
                  {Object.keys(groupedResults).length}
                </div>
                <div className="text-xs text-gray-600">Categories</div>
              </div>
              <div>
                <div className="text-lg font-bold text-orange-600">
                  {searchResults.reduce((sum, p) => sum + (p.closingStock || 0), 0).toLocaleString()}
                </div>
                <div className="text-xs text-gray-600">Total Stock</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Results */}
      {searchResults.length === 0 && !loading && (searchQuery || selectedBrand) && (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400 mb-4 text-4xl">🔍</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
          <p className="text-gray-500">
            Try adjusting your search criteria or check if the date has inventory data.
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Searching inventory data...</p>
        </div>
      )}
    </div>
  );
};

export default InventorySearch;
